import React, { useState, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import cogoToast from 'cogo-toast';
import { SaveBanco } from '../../../../api/Api';
import { useGetUser } from '../../../../Hooks/CustomHooks';

export default function AgregarBancoModal({ showModal, setShowModal, onSuccess }) {
    const user = useGetUser();
    const queryClient = useQueryClient();

    const [codigo, setCodigo] = useState('');
    const [nombre, setNombre] = useState('');
    const [departamento, setDepartamento] = useState('');
    const [distrito, setDistrito] = useState('');
    const [direccion, setDireccion] = useState('');
    const [emailsContacto, setEmailsContacto] = useState('');

    const resetForm = useCallback(() => {
        setCodigo('');
        setNombre('');
        setDepartamento('');
        setDistrito('');
        setDireccion('');
        setEmailsContacto('');
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        setShowModal(false);
    }, [resetForm, setShowModal]);

    const { mutate: mutateSaveBanco, isLoading } = useMutation(
        "saveNuevoBanco",
        SaveBanco,
        {
            onSuccess: (res) => {
                if (res?.data?.success || res?.status === 200 || res?.status === 201) {
                    cogoToast.success('Banco registrado exitosamente.', { position: 'top-right' });
                    queryClient.invalidateQueries("getBancoListHabilitadosEdicion");
                    queryClient.invalidateQueries("getBancoListHabilitadosxCartaFianza");
                    handleClose();
                    if (onSuccess) onSuccess();
                } else {
                    cogoToast.error(res?.data?.message || 'Error al guardar el banco.', { position: 'top-right' });
                }
            },
            onError: (err) => {
                console.error(err);
                cogoToast.error('Error al guardar el banco: ' + (err?.response?.data?.message || err.message), { position: 'top-right' });
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!codigo.trim()) {
            cogoToast.warn('Por favor ingrese el código del banco.', { position: 'top-right' });
            return;
        }

        if (!nombre.trim()) {
            cogoToast.warn('Por favor ingrese el nombre del banco.', { position: 'top-right' });
            return;
        }

        const payload = {
            codigo: codigo.trim(),
            nombre: nombre.trim(),
            codigo_departamento: departamento.trim() || null,
            codigoDepartamento: departamento.trim() || null,
            distrito: distrito.trim() || null,
            direccion: direccion.trim() || null,
            emails_contacto: emailsContacto.trim() || null,
            emailsContacto: emailsContacto.trim() || null,
            updated_by: user?.email || 'System'
        };

        mutateSaveBanco(payload);
    };

    return (
        <Modal show={showModal} onHide={handleClose} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold fs-4">Agregar Nuevo Banco</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="px-4 py-3">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label className="fw-bold">Código del Banco <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej. BCP, BBVA, INTERBANK"
                                    value={codigo}
                                    onChange={(e) => setCodigo(e.target.value)}
                                    maxLength={50}
                                    disabled={isLoading}
                                    required
                                />
                            </Form.Group>
                        </div>

                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label className="fw-bold">Nombre del Banco <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej. Banco de Crédito del Perú"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    maxLength={150}
                                    disabled={isLoading}
                                    required
                                />
                            </Form.Group>
                        </div>

                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label className="fw-bold">Código de Departamento</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej. LIMA, AREQUIPA, IQUITOS"
                                    value={departamento}
                                    onChange={(e) => setDepartamento(e.target.value)}
                                    maxLength={100}
                                    disabled={isLoading}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-md-6">
                            <Form.Group>
                                <Form.Label className="fw-bold">Distrito</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej. San Isidro, Chiclayo, La Victoria"
                                    value={distrito}
                                    onChange={(e) => setDistrito(e.target.value)}
                                    maxLength={100}
                                    disabled={isLoading}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12">
                            <Form.Group>
                                <Form.Label className="fw-bold">Dirección</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej. Av. Juan De Arona N° 893"
                                    value={direccion}
                                    onChange={(e) => setDireccion(e.target.value)}
                                    maxLength={250}
                                    disabled={isLoading}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12">
                            <Form.Group>
                                <Form.Label className="fw-bold">Emails de Contacto</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ej. contacto@banco.com"
                                    value={emailsContacto}
                                    onChange={(e) => setEmailsContacto(e.target.value)}
                                    maxLength={250}
                                    disabled={isLoading}
                                />
                            </Form.Group>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading ? 'Guardando...' : 'Guardar Banco'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
